import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContextLazy'
import { EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, BriefcaseIcon, ShieldCheckIcon, DevicePhoneMobileIcon, FingerPrintIcon } from '@heroicons/react/24/outline'
import { generateDeviceHash } from '../../lib/device-fingerprint'
import { supabase } from '../../lib/supabase-auth'
import { checkUserMFAStatus } from '../../lib/auth-helpers'
import { ensureUserRecord } from '../../lib/ensure-user-record'
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable, startAuthentication } from '../../lib/webauthn'

// Utility functions for device info
const getBrowserInfo = () => {
  const ua = navigator.userAgent
  if (ua.indexOf('Chrome') > -1) return 'Chrome'
  if (ua.indexOf('Safari') > -1) return 'Safari'
  if (ua.indexOf('Firefox') > -1) return 'Firefox'
  if (ua.indexOf('Edge') > -1) return 'Edge'
  return 'Unknown'
}

const getOSInfo = () => {
  const platform = navigator.platform
  if (platform.indexOf('Win') > -1) return 'Windows'
  if (platform.indexOf('Mac') > -1) return 'macOS'
  if (platform.indexOf('Linux') > -1) return 'Linux'
  if (platform.indexOf('iPhone') > -1 || platform.indexOf('iPad') > -1) return 'iOS'
  if (platform.indexOf('Android') > -1) return 'Android'
  return 'Unknown'
}

// Determine redirect destination based on user type (staff vs client)
const getRedirectDestination = async (userEmail: string): Promise<string> => {
  try {
    // Check if user is a platform_user (staff member)
    const { data: platformUser } = await supabase
      .from('platform_users')
      .select('id')
      .eq('email', userEmail)
      .limit(1)

    if (platformUser && platformUser.length > 0) {
      // Staff member - go to clients page
      return '/portal/clients'
    }

    // Not staff - check if they're a client (have a party record)
    const { data: party } = await supabase
      .from('parties')
      .select('id')
      .eq('primary_email', userEmail)
      .limit(1)

    if (party && party.length > 0) {
      // Client - go to client dashboard
      return '/portal/client/dashboard'
    }

    // Default fallback
    return '/portal/clients'
  } catch {
    return '/portal/clients'
  }
}

export const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loginSuccess, setLoginSuccess] = useState(false)
  const [deviceHash, setDeviceHash] = useState<string | null>(null)
  const [showMFA, setShowMFA] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const [tempSession, setTempSession] = useState<any>(null)
  const [trustDevice, setTrustDevice] = useState(false)
  const [webauthnSupported, setWebauthnSupported] = useState(false)
  const [platformAvailable, setPlatformAvailable] = useState(false)
  const [webauthnLoading, setWebauthnLoading] = useState(false)
  const [checkingTrustedDevice, setCheckingTrustedDevice] = useState(false)
  const { signIn, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Initialize device fingerprinting and WebAuthn
  useEffect(() => {
    const initializeDevice = async () => {
      try {
        const hash = await generateDeviceHash()
        setDeviceHash(hash)
        // Store in sessionStorage for use by edge functions
        sessionStorage.setItem('device_hash', hash)
        if (import.meta.env.DEV) {
          console.log('🔐 Device hash generated and stored:', hash.substring(0, 8) + '...')
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to generate device hash:', error)
        }
      }
    }
    
    const checkWebAuthnSupport = async () => {
      const supported = isWebAuthnSupported()
      setWebauthnSupported(supported)
      
      if (supported) {
        const platform = await isPlatformAuthenticatorAvailable()
        setPlatformAvailable(platform)
      }
    }
    
    initializeDevice()
    checkWebAuthnSupport()
    
    // Clear any MFA setup data when landing on login page
    // This ensures users can always access the login page
    sessionStorage.removeItem('pending_mfa_setup')
    sessionStorage.removeItem('mfa_referrer')
  }, [])
  
  // Redirect if already authenticated
  useEffect(() => {
    if (user && !authLoading && !loginSuccess && !showMFA) {
      // Determine correct redirect based on user type
      const doRedirect = async () => {
        const destination = await getRedirectDestination(user.email || '')
        navigate(destination, { replace: true })
      }
      doRedirect()
    }
  }, [user, authLoading, loginSuccess, navigate, showMFA])

  // Navigate when user is authenticated after login
  useEffect(() => {
    if (loginSuccess && user && !authLoading) {
      // If there's a specific location they were trying to access, use that
      // Otherwise determine based on user type
      const doNavigate = async () => {
        const from = location.state?.from?.pathname
        if (from && from !== '/portal/clients') {
          navigate(from, { replace: true })
        } else {
          const destination = await getRedirectDestination(user.email || '')
          navigate(destination, { replace: true })
        }
        // Reset form state after navigation
        setLoading(false)
        setLoginSuccess(false)
      }
      doNavigate()
    }
  }, [user, authLoading, loginSuccess, navigate, location])

  // Check for error state from redirect (e.g., expired link, access denied)
  useEffect(() => {
    const state = location.state as { error?: string } | null
    if (state?.error) {
      setError(state.error)
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, '', location.pathname)
    }
  }, [location])

  // Detect password manager extensions
  React.useEffect(() => {
    const detectExtensions = () => {
      const extensionIndicators = [
        document.querySelector('[data-lastpass-root]'),
        document.querySelector('[data-1password-root]'),
        document.querySelector('[data-bitwarden-root]'),
        document.querySelector('[data-dashlane-root]'),
        // Check for common extension-injected classes
        document.querySelector('.com-1password-button'),
        document.querySelector('.lastpass-button')
      ]
      
      if (extensionIndicators.some(el => el !== null)) {
        // console.log('Password manager extension detected - form protection enabled')
      }
    }
    
    // Check after a delay to allow extensions to inject
    const timer = setTimeout(detectExtensions, 1000)
    return () => clearTimeout(timer)
  }, [])

  const handleWebAuthnLogin = async () => {
    setWebauthnLoading(true)
    setError(null)

    try {
      // Start WebAuthn authentication - let the browser show available passkeys
      // No need to pre-fetch credential IDs - the browser handles discoverable credentials
      const { credential, credentialId, signature, authenticatorData, clientDataJSON } = await startAuthentication({
        // Don't provide credential IDs - let browser show all available passkeys for this domain
        userVerification: 'preferred'
      })

      if (!credential || !credentialId) {
        setError('Authentication cancelled')
        setWebauthnLoading(false)
        return
      }

      if (import.meta.env.DEV) {
        console.log('WebAuthn credential obtained:', credentialId.substring(0, 20) + '...')
      }

      // Verify with edge function and get session token
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('webauthn-verify', {
        body: {
          credentialId,
          signature,
          authenticatorData,
          clientDataJSON,
          createSession: true
        }
      })

      if (verifyError) {
        console.error('WebAuthn verify error:', verifyError)
        setError('Failed to verify passkey. Please try again or use email/password.')
        setWebauthnLoading(false)
        return
      }

      if (!verifyData?.success) {
        setError(verifyData?.error || 'Passkey not recognized. You may need to set up a passkey first.')
        setWebauthnLoading(false)
        return
      }

      if (import.meta.env.DEV) {
        console.log('WebAuthn verified for user:', verifyData.userEmail)
      }

      // Use the session token to authenticate
      if (verifyData.session?.token && verifyData.session?.email) {
        // Use verifyOtp with the token from the magic link
        const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
          email: verifyData.session.email,
          token: verifyData.session.token,
          type: 'magiclink'
        })

        if (sessionError) {
          console.error('Session creation error:', sessionError)
          setError('Failed to create session. Please try again.')
          setWebauthnLoading(false)
          return
        }

        if (sessionData?.session) {
          // Store email for future passkey logins
          localStorage.setItem('last_login_email', verifyData.userEmail)

          // Mark login as successful
          setLoginSuccess(true)

          if (import.meta.env.DEV) {
            console.log('Passkey login successful!')
          }

          // Redirect to portal
          const from = location.state?.from?.pathname || '/portal/clients'
          navigate(from, { replace: true })
          return
        }
      }

      // Fallback: if session creation didn't work, inform the user
      setError('Passkey verified but session creation failed. Please use email/password.')

    } catch (err: any) {
      console.error('WebAuthn error:', err)
      if (err.name === 'NotAllowedError') {
        setError('Authentication was cancelled or timed out')
      } else if (err.name === 'SecurityError') {
        setError('Passkey authentication not available on this connection')
      } else if (err.message?.includes('No credentials')) {
        setError('No passkeys found for this device. Please log in with email/password first, then set up a passkey.')
      } else {
        setError('Failed to authenticate with passkey. Please try email/password.')
      }
    } finally {
      setWebauthnLoading(false)
    }
  }

  const handleMFAVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setMfaLoading(true)
    setError(null)

    try {
      // CRITICAL: Use the session's user_metadata which has the TOTP secret
      const hasTOTPSecret = !!tempSession?.user?.user_metadata?.totp_secret
      
      if (!hasTOTPSecret) {
        if (import.meta.env.DEV) {
          console.error('❌ No TOTP secret found in session during verification')
          console.log('- Session metadata:', tempSession?.user?.user_metadata)
        }
        // User doesn't have MFA set up - force them to set it up
        navigate('/mfa-setup')
        return
      }

      // First try Supabase's built-in MFA verification
      let mfaVerified = false
      
      // Get MFA factors
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      if (currentUser) {
        const { data: factors } = await supabase.auth.mfa.listFactors()
        const totpFactor = factors?.totp?.[0]
        
        if (totpFactor) {
          // Try to verify with Supabase MFA
          const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
            factorId: totpFactor.id
          })
          
          if (!challengeError && challengeData) {
            const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
              factorId: totpFactor.id,
              challengeId: challengeData.id,
              code: mfaCode
            })
            
            if (!verifyError && verifyData) {
              mfaVerified = true
              if (import.meta.env.DEV) {
                console.log('✅ MFA verified with Supabase built-in system')
              }
            }
          }
        }
      }
      
      // Fallback to custom TOTP verification if Supabase MFA fails
      if (!mfaVerified && tempSession.user.user_metadata.totp_secret) {
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-totp', {
          body: { 
            secret: tempSession.user.user_metadata.totp_secret,
            code: mfaCode 
          }
        })

        if (verifyError || !verifyData?.valid) {
          setError('Invalid verification code. Please try again.')
          setMfaLoading(false)
          return
        }
        
        mfaVerified = true
        if (import.meta.env.DEV) {
          console.log('✅ MFA verified with custom TOTP system')
        }
      }
      
      if (!mfaVerified) {
        setError('Unable to verify code. Please try again.')
        setMfaLoading(false)
        return
      }

      // If user wants to trust device, register it AFTER successful MFA
      if (trustDevice && deviceHash) {
        if (import.meta.env.DEV) {
          console.log('📱 Registering trusted device:', {
            deviceHash,
            browser: getBrowserInfo(),
            os: getOSInfo(),
            trustDuration: 180 // 180 days as shown in UI
          })
        }
        
        const { data: deviceData, error: deviceError } = await supabase.functions.invoke('register-device', {
          body: {
            deviceHash,
            deviceName: `${getBrowserInfo()} on ${getOSInfo()}`,
            metadata: {
              platform: navigator.platform,
              browser: getBrowserInfo(),
              os: getOSInfo(),
              userAgent: navigator.userAgent,
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              language: navigator.language
            },
            trustDuration: 180 // 180 days to match UI
          },
          headers: {
            'x-device-hash': deviceHash
          }
        })
        
        if (deviceError) {
          console.error('❌ Failed to register device:', deviceError)
        } else if (deviceData?.success) {
          if (import.meta.env.DEV) {
            console.log('✅ Device successfully registered:', {
              deviceId: deviceData.deviceId,
              expiresAt: deviceData.expiresAt,
              message: deviceData.message
            })
          }
        } else {
          console.error('❌ Device registration failed:', deviceData)
        }
      } else if (import.meta.env.DEV) {
        console.log('⚠️ Device not registered:', {
          trustDevice,
          hasDeviceHash: !!deviceHash,
          reason: !trustDevice ? 'User did not check trust device' : 'No device hash available'
        })
      }

      // MFA verified successfully - now we can safely redirect
      // The session was stored temporarily, now mark it as MFA verified
      sessionStorage.setItem('mfa_verified', 'true')
      
      // Clean up pending MFA setup
      sessionStorage.removeItem('pending_mfa_setup')
      sessionStorage.removeItem('mfa_referrer')
      
      // CRITICAL FIX: Use window.location.replace to prevent redirect loops
      // This ensures the browser doesn't cache the MFA verification state incorrectly
      window.location.replace('/portal/clients')
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('MFA verification error:', err)
      }
      setError('Verification failed. Please try again.')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation() // Stop event from bubbling to extension handlers
    
    // Disable form during submission to prevent multiple submits and extension interference
    const form = e.currentTarget as HTMLFormElement
    const formElements = form.querySelectorAll('input, button')
    formElements.forEach(el => {
      (el as HTMLInputElement | HTMLButtonElement).disabled = true
    })
    
    setError(null)
    setLoading(true)

    try {
      const { error, requiresMFA, session } = await signIn(email, password, rememberMe)
      
      if (error) {
        setError(error.message)
        setLoading(false)
      } else if (!requiresMFA) {
        // Trusted device - MFA not required, login successful
        if (import.meta.env.DEV) {
          console.log('✅ Trusted device - completing login without MFA')
        }
        
        // Store email for future WebAuthn login
        if (rememberMe) {
          localStorage.setItem('last_login_email', email)
        } else {
          sessionStorage.setItem('last_login_email', email)
        }
        
        // Mark login as successful
        setLoginSuccess(true)
        setLoading(false)
        
        // Navigation will be handled by the useEffect that watches loginSuccess
      } else if (requiresMFA) {
        // Ensure user record exists in database
        if (session?.user) {
          await ensureUserRecord(session.user)
        }
        
        // MFA is required - device trust was already checked in AuthContext
        // Show loading state while checking
        setCheckingTrustedDevice(true)
        setLoading(false) // Stop the main loading spinner
        
        // Store the session temporarily - do NOT trigger any navigation yet
        setTempSession(session)
        
        // Device trust check already happened in AuthContext.signIn()
        // If we're here, it means the device is NOT trusted or MFA is required by policy
        // Check if user has MFA set up - CRITICAL: Force refresh to get latest from database
        // The session JWT metadata might be stale if user just set up MFA
        const mfaStatus = await checkUserMFAStatus(session.user.id, session.user.user_metadata, true)
        
        // Trust the MFA status check result - it fetches fresh data from the database
        const userHasMFA = mfaStatus.hasMFA
        
        if (import.meta.env.DEV) {
          console.log('🔐 MFA Check Debug:')
          console.log('- Session user:', session?.user?.email)
          console.log('- Session has MFA metadata:', !!(session?.user?.user_metadata?.totp_secret || session?.user?.user_metadata?.mfa_enabled))
          console.log('- MFA status check result:', mfaStatus)
          console.log('- User has MFA (final):', userHasMFA)
        }
        
        // Stop checking state AFTER we've done all async operations
        setCheckingTrustedDevice(false)
        
        if (!userHasMFA) {
          // User needs to set up MFA first
          // Store session info and redirect to MFA setup
          sessionStorage.setItem('pending_mfa_setup', JSON.stringify({
            email: email,
            session: session,
            timestamp: Date.now()
          }))
          // Store where we came from so back button works
          sessionStorage.setItem('mfa_referrer', '/login')
          
          // Stop loading state before navigating
          setLoading(false)
          
          // Use window.location for more reliable navigation
          window.location.href = '/mfa-setup'
          return
        }
        
        // IMPORTANT: If we reach here, user HAS MFA set up
        // For users with MFA and device not trusted, we should show the MFA form HERE
        // NOT redirect to /mfa-setup which causes the flash
        
        // Store the session temporarily for MFA verification
        setTempSession(session)
        
        // Also store in sessionStorage in case ProtectedRoute redirects to /mfa-setup
        sessionStorage.setItem('pending_mfa_setup', JSON.stringify({
          email: email,
          session: session,
          timestamp: Date.now()
        }))
        sessionStorage.setItem('mfa_referrer', '/login')
        
        // User has MFA already set up, NOW show verification
        // Session already stored above
        if (import.meta.env.DEV) {
          console.log('🎯 SETTING showMFA to TRUE - User should see verification form')
          console.log('- Email:', email)
          console.log('- Session exists:', !!session)
          console.log('- TempSession set:', !!tempSession)
        }
        setShowMFA(true)
        setLoading(false)
        setError(null)
        
        // Add a small delay to ensure state updates properly
        setTimeout(() => {
          if (import.meta.env.DEV) {
            console.log('🔍 After state update - showMFA should be true:', showMFA)
          }
        }, 100)
      } else {
        // No MFA required - proceed with login
        // Store email for future WebAuthn login
        if (rememberMe) {
          localStorage.setItem('last_login_email', email)
        } else {
          sessionStorage.setItem('last_login_email', email)
        }
        setLoginSuccess(true)
      }
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Login error:', err);
        }

      }
      setError('An unexpected error occurred. Please try again.')
      setLoading(false)
    } finally {
      // Re-enable form elements after submission
      formElements.forEach(el => {
        (el as HTMLInputElement | HTMLButtonElement).disabled = false
      })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800 py-4 px-4 sm:px-6 lg:px-8">
      {/* Loading overlay for post-login redirect */}
      {loginSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-900 font-medium">Redirecting to your portal...</p>
          </div>
        </div>
      )}
      
      {/* Background Pattern - Dots */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      <div className="max-w-md lg:max-w-xl w-full space-y-3 lg:space-y-4 relative z-10">
        {/* Logo and Branding */}
        <div className="text-center flex flex-col items-center">
          <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2">Professional Access</p>
          <Link to="/" className="block -ml-6">
            <img 
              className="h-20 lg:h-24 w-auto mb-3 cursor-pointer hover:opacity-90 transition-opacity" 
              src="/assets/Brands/PCM Logo White.png" 
              alt="PCM Paslawski Capital Management" 
            />
          </Link>
          <div className="flex items-center gap-2 mb-1">
            <BriefcaseIcon className="h-6 w-6 text-blue-300" />
            <h2 className="text-2xl lg:text-3xl font-bold text-white">
              Advisor Portal
            </h2>
          </div>
          <p className="text-sm lg:text-base text-gray-300">
            Manage clients & investment portfolios
          </p>
          <div className="w-16 lg:w-20 h-1 bg-blue-400 mx-auto mt-3"></div>
        </div>
        
        {/* Login Card */}
        <div className="bg-blue-50/95 backdrop-blur-sm rounded-xl shadow-2xl border border-blue-100 p-5 lg:p-8">
          {!showMFA ? (
            <>
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">
                Welcome back
              </h3>
              <form className="space-y-3 lg:space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-3">
              <div>
                <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="username email"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bw-ignore="true"
                    required
                    className="appearance-none relative block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all duration-200 sm:text-sm"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bw-ignore="true"
                    required
                    className="appearance-none relative block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all duration-200 sm:text-sm"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {loginSuccess ? 'Redirecting...' : 'Signing in...'}
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            {/* WebAuthn Login Button */}
            {platformAvailable && (
              <div className="mt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-blue-50 text-gray-500">Or use biometrics</span>
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={handleWebAuthnLogin}
                  disabled={webauthnLoading || loading}
                  className="mt-4 group relative w-full flex justify-center items-center py-2.5 px-4 border border-blue-300 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {webauthnLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Authenticating...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <FingerPrintIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Sign in with Face ID / Touch ID
                    </div>
                  )}
                </button>
              </div>
            )}

            <div className="mt-3 lg:mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-blue-50 text-gray-500">Are you a client?</span>
                </div>
              </div>

              <p className="mt-4 text-center text-sm text-gray-600">
                Clients can sign in using the form above. You'll be automatically directed to your dashboard.
              </p>
            </div>
          </form>
            </>
          ) : checkingTrustedDevice ? (
            <>
              <div className="relative">
                {/* Backdrop overlay for loading state */}
                <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-xl z-10"></div>
                
                {/* Loading content */}
                <div className="relative z-20 flex flex-col items-center justify-center py-16">
                  {/* Animated security shield */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-blue-100 rounded-full blur-xl animate-pulse"></div>
                    <div className="relative bg-white rounded-full p-4 shadow-lg">
                      <ShieldCheckIcon className="h-12 w-12 text-blue-600" />
                    </div>
                    {/* Rotating ring */}
                    <div className="absolute -inset-4 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"></div>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Verifying Security
                  </h3>
                  <p className="text-sm text-gray-600 mb-6 text-center max-w-xs">
                    Checking if this is a trusted device...
                  </p>
                  
                  {/* Progress dots */}
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </>
          ) : showMFA ? (
            <>
              {import.meta.env.DEV && console.log('🚀 RENDERING MFA VERIFICATION FORM - showMFA is true')}
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900">
                  Two-Factor Authentication
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Enter the 6-digit code from your authenticator app to continue.
              </p>
              <form className="space-y-4" onSubmit={handleMFAVerify}>
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="mfa-code"
                      name="mfa-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      className="appearance-none relative block w-full px-3 py-2.5 bg-white border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all duration-200 font-mono text-xl tracking-wider text-center"
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={mfaLoading}
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800">
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    id="trust-device"
                    name="trust-device"
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                  />
                  <label htmlFor="trust-device" className="ml-2 block text-sm text-gray-700">
                    Trust this device for 180 days
                  </label>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={mfaLoading || mfaCode.length !== 6}
                    className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {mfaLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </div>
                    ) : (
                      'Verify & Continue'
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShowMFA(false)
                      setMfaCode('')
                      setError(null)
                    }}
                    className="w-full text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Back to login
                  </button>
                </div>
              </form>
            </>
          ) : null}
        </div>
        
        
        {/* Footer */}
        <div className="text-center text-sm text-gray-400">
          <p className="mb-2">
            Empowering Saskatchewan & Alberta's agricultural and business leaders
          </p>
          <p>
            © {new Date().getFullYear()} PCM Paslawski Capital Management. All rights reserved.
          </p>
          <p className="mt-2">
            Questions? Reach out at{' '}
            <a href="mailto:support@pcmfinancial.ca" className="text-amber-400 hover:text-amber-300 transition-colors">
              support@pcmfinancial.ca
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}